import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Layouts from "../../config/layouts/Layouts";
import NavbarComponent from "../../components/navbar/Navbar";
import Footer from "../../components/footer/Footer";
import {
    PROYECTOS,
    CASOS_EXITO,
    FILTROS_CASOS,
} from "../../data/proyectosData";
import { MotionDiv, StaggerGrid, StaggerItem, fadeInUp } from "../../utils/animations";
import "../../components/proyectos/proyectos.css";

function CasoImagen({ src, alt }) {
    const [failed, setFailed] = useState(false);

    if (failed) {
        return (
            <div className="proyectos-caso-img-fallback" aria-hidden>
                Imagen no disponible
            </div>
        );
    }

    return (
        <img
            className="proyectos-caso-img"
            src={src}
            alt={alt}
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
        />
    );
}

function Proyectos() {
    const [filtro, setFiltro] = useState("All");

    const casosFiltrados = useMemo(() => {
        if (filtro === "All") return CASOS_EXITO;
        return CASOS_EXITO.filter((c) => c.categoria === filtro);
    }, [filtro]);

    return (
        <Layouts>
            <header className="shrink-0">
                <NavbarComponent />
            </header>

            <main className="proyectos-page">
                <div className="proyectos-container">
                    <MotionDiv className="proyectos-hero">
                        <h1 className="proyectos-title">Nuestros proyectos</h1>
                        <p className="proyectos-subtitle">
                            Productos propios, casos de éxito y soluciones que
                            hemos construido junto a nuestros clientes en web,
                            móvil, IA y ciberseguridad.
                        </p>
                    </MotionDiv>

                    <MotionDiv delay={0.1}>
                        <h2 className="proyectos-section-title">Apps publicadas</h2>
                    </MotionDiv>
                    <StaggerGrid className="proyectos-apps-grid">
                        {PROYECTOS.map((p) => (
                            <StaggerItem key={p.nombre}>
                                <motion.article
                                    className="proyectos-app-card"
                                    whileHover={{ y: -6, scale: 1.01 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                >
                                    <div className="proyectos-app-img-wrap">
                                        <img
                                            className="proyectos-app-img"
                                            src={p.imagen}
                                            alt=""
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </div>
                                    <div className="proyectos-app-body">
                                        <span className="proyectos-app-tipo">{p.tipo}</span>
                                        <h3 className="proyectos-app-nombre">{p.nombre}</h3>
                                        <p className="proyectos-app-desc">{p.descripcion}</p>
                                        {p.casePath ? (
                                            <Link className="proyectos-app-link" to={p.casePath}>Ver caso de estudio →</Link>
                                        ) : null}
                                        {p.urlWeb ? (
                                            <a
                                                className="proyectos-app-link"
                                                href={p.urlWeb}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Ver sitio web →
                                            </a>
                                        ) : null}
                                        {p.url ? (
                                            <a
                                                className="proyectos-app-link"
                                                href={p.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Ver en Play Store →
                                            </a>
                                        ) : null}
                                    </div>
                                </motion.article>
                            </StaggerItem>
                        ))}
                    </StaggerGrid>

                    <MotionDiv className="mt-8 rounded-2xl border border-em-text/10 bg-em-card p-6 md:p-8" delay={0.12}>
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-em-accent2">
                            Producto con inteligencia artificial
                        </p>
                        <div className="mt-3 grid gap-5 md:grid-cols-[1fr_auto] md:items-end">
                            <div>
                                <h2 className="text-2xl font-black text-em-text">
                                    Análisis Presidencial CO 2026
                                </h2>
                                <p className="mt-2 max-w-3xl leading-7 text-em-soft">
                                    Aplicación desarrollada por EMMPIEZA que consulta fuentes públicas y combina
                                    búsqueda con ChatGPT, Gemini y Perplexity para presentar análisis verificables.
                                </p>
                            </div>
                            <a
                                className="proyectos-app-link"
                                href="https://play.google.com/store/apps/details?id=com.emmpieza.radarpresidencial"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Ver ficha en Google Play →
                            </a>
                            <Link className="proyectos-app-link" to="/casos/analisis-presidencial-colombia-2026">
                                Ver caso de estudio →
                            </Link>
                        </div>
                    </MotionDiv>

                    <MotionDiv delay={0.15}>
                        <h2 className="proyectos-section-title">Casos de éxito</h2>
                    </MotionDiv>
                    <motion.div
                        className="proyectos-filters"
                        role="tablist"
                        aria-label="Filtrar por categoría"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        {FILTROS_CASOS.map((f) => (
                            <button
                                key={f}
                                type="button"
                                role="tab"
                                aria-selected={filtro === f}
                                className={`proyectos-filter-btn ${filtro === f ? "is-active" : ""}`}
                                onClick={() => setFiltro(f)}
                            >
                                {f === "All" ? "Todos" : f}
                            </button>
                        ))}
                    </motion.div>

                    <StaggerGrid className="proyectos-casos-grid" key={filtro}>
                        {casosFiltrados.length === 0 ? (
                            <p className="proyectos-empty">No hay casos en esta categoría.</p>
                        ) : (
                            casosFiltrados.map((c) => (
                                <StaggerItem key={`${c.titulo}-${c.categoria_tag}`}>
                                    <motion.article
                                        className="proyectos-caso-card"
                                        whileHover={{ y: -6 }}
                                        transition={{ type: "spring", stiffness: 300, damping: 22 }}
                                    >
                                        <div className="proyectos-caso-img-wrap">
                                            <CasoImagen src={c.imagen} alt={c.titulo} />
                                        </div>
                                        <div className="proyectos-caso-body">
                                            <div className="proyectos-caso-tags">
                                                <span className="proyectos-caso-cat">{c.categoria_tag}</span>
                                                <span className="proyectos-caso-metrica">
                                                    <strong>{c.metrica}</strong>
                                                    {" · "}
                                                    {c.metrica_etiqueta}
                                                </span>
                                            </div>
                                            <h3 className="proyectos-caso-titulo">{c.titulo}</h3>
                                            <dl className="proyectos-caso-block">
                                                <dt>Reto</dt>
                                                <dd>{c.reto}</dd>
                                            </dl>
                                            <dl className="proyectos-caso-block">
                                                <dt>Solución</dt>
                                                <dd>{c.solucion}</dd>
                                            </dl>
                                            <div className="proyectos-caso-actions">
                                                {c.casePath ? (
                                                    <Link className="proyectos-caso-link proyectos-caso-link--outline" to={c.casePath}>
                                                        Caso de estudio
                                                    </Link>
                                                ) : null}
                                                {c.urlandroid && c.urlios ? (
                                                    <>
                                                        <a
                                                            className="proyectos-caso-link"
                                                            href={c.urlandroid}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            Android
                                                        </a>
                                                        <a
                                                            className="proyectos-caso-link proyectos-caso-link--outline"
                                                            href={c.urlios}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            iOS
                                                        </a>
                                                    </>
                                                ) : (
                                                    <a
                                                        className="proyectos-caso-link"
                                                        href={c.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Ver proyecto →
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </motion.article>
                                </StaggerItem>
                            ))
                        )}
                    </StaggerGrid>
                </div>
            </main>

            <Footer />
        </Layouts>
    );
}

export default Proyectos;
